import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// BugHerd-style element snipper for the feedback capture (handoff 14).
//
// Given a LIVE tab MediaStream (from getDisplayMedia({ preferCurrentTab:true })),
// the user hovers the page to outline the element under the cursor and clicks to
// grab a pixel-accurate screenshot of just that element. Press F for the whole
// page, Esc (or right-click) to cancel.
//
// Why a media stream and not html2canvas / SVG-foreignObject: this app is
// map-heavy and the Google tiles are cross-origin, so a DOM rasteriser blanks or
// taints them. The tab stream captures composited pixels, so the map comes
// through, and we simply crop to the element's viewport rect.
//
// The feedback modal is hidden by its parent while this is active, so it is never
// in the shot. Our own outline + hint are hidden for a couple of frames right
// before each frame grab so they are not captured either. The overlay itself is
// fully transparent (no dim), so it leaves no trace in the capture.

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export default function ElementSnip({ stream, onCapture, onCancel }) {
  const [ready, setReady] = useState(false);
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const boxRef = useRef(null);
  const hintRef = useRef(null);
  const hoveredRef = useRef(null);
  const busyRef = useRef(false);

  const stop = useCallback(() => {
    try { if (stream) stream.getTracks().forEach((t) => t.stop()); } catch (_) { /* stream already gone */ }
    const v = videoRef.current;
    if (v) { try { v.pause(); } catch (_) { /* ignore */ } v.srcObject = null; }
  }, [stream]);

  const cancel = useCallback(() => { stop(); onCancel?.(); }, [stop, onCancel]);

  // Grab the current video frame and crop it to `rect` (viewport CSS px).
  const grab = useCallback((rect) => {
    if (busyRef.current) return;
    const v = videoRef.current;
    if (!v || !v.videoWidth) { cancel(); return; }
    busyRef.current = true;
    // Hide our own affordances so they are not in the shot, then wait a couple
    // of frames for the capture stream to reflect that before drawing.
    if (boxRef.current) boxRef.current.style.display = 'none';
    if (hintRef.current) hintRef.current.style.display = 'none';
    setTimeout(() => {
      try {
        const iw = window.innerWidth;
        const ih = window.innerHeight;
        const sX = v.videoWidth / iw;
        const sY = v.videoHeight / ih;
        const x0 = clamp(rect.left, 0, iw);
        const y0 = clamp(rect.top, 0, ih);
        const x1 = clamp(rect.right, 0, iw);
        const y1 = clamp(rect.bottom, 0, ih);
        const cw = Math.max(1, Math.round((x1 - x0) * sX));
        const ch = Math.max(1, Math.round((y1 - y0) * sY));
        const cv = document.createElement('canvas');
        cv.width = cw;
        cv.height = ch;
        cv.getContext('2d').drawImage(v, Math.round(x0 * sX), Math.round(y0 * sY), cw, ch, 0, 0, cw, ch);
        stop();
        cv.toBlob((b) => {
          if (b) onCapture?.(new File([b], 'element-capture.png', { type: 'image/png' }));
          else onCancel?.();
        }, 'image/png');
      } catch (_) {
        cancel();
      }
    }, 130);
  }, [cancel, onCapture, onCancel, stop]);

  // Start playback, wait for real dimensions, then arm the picker. Also bail if
  // the user stops sharing from the browser's own capture UI.
  useEffect(() => {
    let alive = true;
    const v = document.createElement('video');
    v.muted = true;
    v.playsInline = true;
    v.srcObject = stream;
    videoRef.current = v;
    const arm = () => { if (alive && v.videoWidth) setReady(true); };
    v.addEventListener('loadedmetadata', arm);
    v.play().then(arm).catch(() => { if (alive) cancel(); });
    const track = stream && stream.getVideoTracks && stream.getVideoTracks()[0];
    if (track) track.addEventListener('ended', cancel);
    return () => {
      alive = false;
      v.removeEventListener('loadedmetadata', arm);
      if (track) track.removeEventListener('ended', cancel);
    };
  }, [stream, cancel]);

  // Esc cancels; F captures the whole page. Capture phase + stopPropagation so
  // the host feedback modal's own key handlers stay dormant while picking.
  useEffect(() => {
    if (!ready) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancel(); }
      else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault(); e.stopPropagation();
        grab({ left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight });
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [ready, cancel, grab]);

  // Hit-test the page beneath the (transparent) overlay by briefly disabling the
  // overlay's own pointer capture. Keeping the overlay interactive otherwise
  // means the page never receives stray hover/click while picking.
  const hitTest = useCallback((x, y) => {
    const ov = overlayRef.current;
    if (!ov) return null;
    const prev = ov.style.pointerEvents;
    ov.style.pointerEvents = 'none';
    const el = document.elementFromPoint(x, y);
    ov.style.pointerEvents = prev || 'auto';
    return el;
  }, []);

  const onMouseMove = useCallback((e) => {
    if (busyRef.current) return;
    const el = hitTest(e.clientX, e.clientY);
    hoveredRef.current = el || null;
    const box = boxRef.current;
    if (!box) return;
    if (!el) { box.style.display = 'none'; return; }
    const r = el.getBoundingClientRect();
    box.style.display = 'block';
    box.style.left = `${r.left}px`;
    box.style.top = `${r.top}px`;
    box.style.width = `${r.width}px`;
    box.style.height = `${r.height}px`;
  }, [hitTest]);

  const onClick = useCallback((e) => {
    if (!ready || busyRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const el = hoveredRef.current || hitTest(e.clientX, e.clientY);
    if (!el) return;
    grab(el.getBoundingClientRect());
  }, [ready, grab, hitTest]);

  return createPortal(
    <div
      ref={overlayRef}
      onMouseMove={onMouseMove}
      onClick={onClick}
      onContextMenu={(e) => { e.preventDefault(); cancel(); }}
      className="fixed inset-0 z-[2147483000] cursor-crosshair"
      style={{ background: 'transparent' }}
      aria-hidden="true"
    >
      <div
        ref={boxRef}
        className="fixed pointer-events-none border-2 border-emerald-500 bg-emerald-400/10 rounded-[3px] shadow-[0_0_0_2px_rgba(16,185,129,0.25)]"
        style={{ display: 'none', left: 0, top: 0, width: 0, height: 0 }}
      />
      {ready && (
        <div
          ref={hintRef}
          className="fixed left-1/2 bottom-6 -translate-x-1/2 pointer-events-none select-none whitespace-nowrap rounded-full bg-slate-900/90 text-white text-[12px] font-medium px-3.5 py-1.5 shadow-lg"
        >
          Click an element to capture{'\u00a0\u00b7\u00a0'}press F for the whole page{'\u00a0\u00b7\u00a0'}Esc to cancel
        </div>
      )}
    </div>,
    document.body,
  );
}
